// src/pages/Admin_Module.jsx

import { useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  Link,
} from "react-router-dom";

import axios from "axios";
import Swal from "sweetalert2";

import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";

import {
  ArrowLeft,
  BadgeCheck,
  Boxes,
  Building2,
  Save as SaveIcon,
  User as UserIcon,
} from "lucide-react";

const API = "https://superadmin.nissiindia.com";

/**
 * Website pages:
 * - Stored in dbo.admin_vpn_modulePages
 *
 * Sentinel mobile-app pages:
 * - Master details from dbo.sentinel_app_pages
 * - Access stored in dbo.sentinel_client_page_access
 */
const MODULE_CATALOG = [
  /* ---------------------------------------------------------------------- */
  /* Main Site Modules                                                      */
  /* ---------------------------------------------------------------------- */

  {
    label: "Dashboard",
    link: "/dashboard",
    id: 1,
    category: "Main Site Modules",
    accessType: "SITE",
  },
  {
    label: "Installation Module",
    link: "/installation",
    id: 2,
    category: "Main Site Modules",
    accessType: "SITE",
  },

  /* ---------------------------------------------------------------------- */
  /* User Details                                                           */
  /* ---------------------------------------------------------------------- */

  {
    label: "User List",
    link: "/user_list",
    id: 101,
    category: "User Details",
    accessType: "SITE",
  },
  {
    label: "Add New User",
    link: "/add_user",
    id: 102,
    category: "User Details",
    accessType: "SITE",
  },
  {
    label: "Sentinel User List",
    link: "/sentinel_user_list",
    id: 103,
    category: "User Details",
    accessType: "SITE",
  },
  {
    label: "User Incident Types",
    link: "/add_incidents",
    id: 104,
    category: "User Details",
    accessType: "SITE",
  },

  /* ---------------------------------------------------------------------- */
  /* Route Management                                                       */
  /* ---------------------------------------------------------------------- */

  {
    label: "Create Route",
    link: "/create_route",
    id: 201,
    category: "Route Management",
    accessType: "SITE",
  },
  {
    label: "Route List",
    link: "/route_list",
    id: 202,
    category: "Route Management",
    accessType: "SITE",
  },
  {
    label: "Place List",
    link: "/place_list",
    id: 203,
    category: "Route Management",
    accessType: "SITE",
  },
  {
    label: "Sentinel Route List",
    link: "/sentinel_route_list",
    id: 204,
    category: "Route Management",
    accessType: "SITE",
  },

  /* ---------------------------------------------------------------------- */
  /* QR Codes                                                               */
  /* ---------------------------------------------------------------------- */

  {
    label: "Patrol QR",
    link: "/patrol-qr",
    id: 301,
    category: "QR Codes",
    accessType: "SITE",
  },
  {
    label: "Sentinel QR",
    link: "/sentinel-qr",
    id: 302,
    category: "QR Codes",
    accessType: "SITE",
  },

  /* ---------------------------------------------------------------------- */
  /* Reporting System                                                       */
  /* ---------------------------------------------------------------------- */

  {
    label: "Summaries Module",
    link: "/summaries",
    id: 401,
    category: "Reporting System",
    accessType: "SITE",
  },
  {
    label: "Analytics Module",
    link: "/analytics",
    id: 402,
    category: "Reporting System",
    accessType: "SITE",
  },
  {
    label: "Export Module",
    link: "/export",
    id: 403,
    category: "Reporting System",
    accessType: "SITE",
  },
  {
    label: "Custom Module",
    link: "/custom",
    id: 404,
    category: "Reporting System",
    accessType: "SITE",
  },

  /* ---------------------------------------------------------------------- */
  /* Sentinel Website Modules                                               */
  /* ---------------------------------------------------------------------- */

  {
    label: "Sentinel Dashboard",
    link: "/sentinel_dashboard",
    id: 501,
    category: "Sentinel Modules",
    accessType: "SITE",
  },
  {
    label: "Sentinel Reports",
    link: "/sentinel_reports",
    id: 502,
    category: "Sentinel Modules",
    accessType: "SITE",
  },
  {
    label: "InCampus Module",
    link: "/incampus",
    id: 503,
    category: "Sentinel Modules",
    accessType: "SITE",
  },

  /* ---------------------------------------------------------------------- */
  /* Sentinel Mobile App Pages                                              */
  /* ---------------------------------------------------------------------- */

  {
    label: "App Dashboard",
    link: "Dashboard",
    id: 1001,
    category: "Sentinel App Pages",
    accessType: "APP",
  },
  {
    label: "App Route List",
    link: "RouteList",
    id: 1002,
    category: "Sentinel App Pages",
    accessType: "APP",
  },
  {
    label: "GPS Patrol",
    link: "GPS",
    id: 1003,
    category: "Sentinel App Pages",
    accessType: "APP",
  },
  {
    label: "QR Scanning",
    link: "Scanning",
    id: 1004,
    category: "Sentinel App Pages",
    accessType: "APP",
  },
  {
    label: "Incidents",
    link: "Incidents",
    id: 1005,
    category: "Sentinel App Pages",
    accessType: "APP",
  },
  {
    label: "Campus Check",
    link: "CampusCheck",
    id: 1006,
    category: "Sentinel App Pages",
    accessType: "APP",
    requiresSelfieVerification: true,
  },
  {
    label: "Attendance",
    link: "Attendance",
    id: 1010,
    category: "Sentinel App Pages",
    accessType: "APP",
    requiresSelfieVerification: true,
  },
  {
    label: "Escort",
    link: "Escort",
    id: 1007,
    category: "Sentinel App Pages",
    accessType: "APP",
  },
  {
    label: "Emergency",
    link: "Emergencies",
    id: 1008,
    category: "Sentinel App Pages",
    accessType: "APP",
  },
  {
    label: "Selfie Verification",
    link: "SelfieVerification",
    id: 1009,
    category: "Sentinel App Pages",
    accessType: "APP",
  },
];

function useTenantId() {
  const location = useLocation();

  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("tid");
  }, [location.search]);
}

function normalizeApproved(value) {
  if (value === true || value === 1) {
    return true;
  }

  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();

  return (
    normalized === "1" ||
    normalized === "true" ||
    normalized === "yes" ||
    normalized === "active"
  );
}

export default function Admin_Module() {
  const { username: routeUsername } = useParams();

  const tenantId = useTenantId();

  const username = useMemo(() => {
    try {
      return decodeURIComponent(routeUsername || "").trim();
    } catch {
      return String(routeUsername || "").trim();
    }
  }, [routeUsername]);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [tenant, setTenant] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [checked, setChecked] = useState({});

  const groupedModules = useMemo(() => {
    return MODULE_CATALOG.reduce((groups, item) => {
      if (!groups[item.category]) {
        groups[item.category] = [];
      }

      groups[item.category].push(item);

      return groups;
    }, {});
  }, []);

  const allChecked = useMemo(() => {
    return (
      MODULE_CATALOG.length > 0 &&
      MODULE_CATALOG.every(
        (module) => checked[module.link] === true
      )
    );
  }, [checked]);

  const selectedCount = useMemo(() => {
    return MODULE_CATALOG.filter(
      (module) => checked[module.link]
    ).length;
  }, [checked]);

  const selectedSiteCount = useMemo(() => {
    return MODULE_CATALOG.filter(
      (module) =>
        module.accessType === "SITE" &&
        checked[module.link]
    ).length;
  }, [checked]);

  const selectedAppCount = useMemo(() => {
    return MODULE_CATALOG.filter(
      (module) =>
        module.accessType === "APP" &&
        checked[module.link]
    ).length;
  }, [checked]);

  useEffect(() => {
    let cancelled = false;

    async function loadModuleAccess() {
      if (!tenantId || !username) {
        return;
      }

      setLoading(true);

      try {
        const [tenantResponse, modulesResponse] =
          await Promise.all([
            axios.get(
              `${API}/api/tenant/basic/${encodeURIComponent(
                tenantId
              )}`
            ),
            axios.get(
              `${API}/api/tenant/mysql/modules/${encodeURIComponent(
                tenantId
              )}/${encodeURIComponent(username)}`
            ),
          ]);

        if (cancelled) {
          return;
        }

        setTenant(
          tenantResponse.data?.tenant ||
            modulesResponse.data?.tenant ||
            null
        );

        setAdmin(
          modulesResponse.data?.admin || null
        );

        const grants =
          modulesResponse.data?.modules || [];

        const nextChecked = {};

        grants.forEach((row) => {
          const link = String(
            row?.links ||
              row?.route_name ||
              ""
          ).trim();

          if (
            link &&
            normalizeApproved(row?.approve)
          ) {
            nextChecked[link] = true;
          }
        });

        setChecked(nextChecked);
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error(
          "[ADMIN MODULE LOAD ERROR]",
          error
        );

        await Swal.fire(
          "Error",
          error?.response?.data?.error ||
            error?.message ||
            "Unable to load module access",
          "error"
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadModuleAccess();

    return () => {
      cancelled = true;
    };
  }, [tenantId, username]);

  const toggleAll = () => {
    if (allChecked) {
      setChecked({});
      return;
    }

    const next = {};

    MODULE_CATALOG.forEach((module) => {
      next[module.link] = true;
    });

    setChecked(next);
  };

  const toggleOne = (link) => {
    setChecked((previous) => {
      const next = {
        ...previous,
        [link]: !previous[link],
      };

      /*
        Campus Check and Attendance require Selfie Verification.

        When either page is selected, Selfie Verification is
        automatically selected as well.
      */
      if (
        next[link] &&
        (
          link === "CampusCheck" ||
          link === "Attendance"
        )
      ) {
        next.SelfieVerification = true;
      }

      /*
        Selfie Verification cannot be removed while a dependent
        app page remains selected.
      */
      if (
        link === "SelfieVerification" &&
        previous.SelfieVerification &&
        (
          previous.CampusCheck ||
          previous.Attendance
        )
      ) {
        next.SelfieVerification = true;

        Swal.fire(
          "Selfie Verification Required",
          "Selfie Verification is required when Campus Check or Attendance is selected.",
          "info"
        );
      }

      return next;
    });
  };

  const toggleCategory = (items) => {
    const categoryAllChecked = items.every(
      (module) => checked[module.link]
    );

    setChecked((previous) => {
      const next = { ...previous };

      items.forEach((module) => {
        next[module.link] =
          !categoryAllChecked;
      });

      if (
        next.CampusCheck ||
        next.Attendance
      ) {
        next.SelfieVerification = true;
      }

      return next;
    });
  };

  const onSave = async (event) => {
    event.preventDefault();

    if (!tenantId) {
      await Swal.fire(
        "Missing tenant",
        "Tenant ID is required.",
        "warning"
      );
      return;
    }

    if (!username) {
      await Swal.fire(
        "Missing admin",
        "Admin username is required.",
        "warning"
      );
      return;
    }

    const selectedModules = MODULE_CATALOG
      .filter(
        (module) => checked[module.link]
      )
      .map((module) => ({
        pg_id: module.id,
        modules: module.label,
        links: module.link,
        category: module.category,
        access_type: module.accessType,
        approve: 1,
      }));

    console.log(
      "[MODULE SAVE PAYLOAD]",
      selectedModules
    );

    setSaving(true);

    try {
      const response = await axios.put(
        `${API}/api/tenant/mysql/modules/${encodeURIComponent(
          tenantId
        )}/${encodeURIComponent(username)}`,
        {
          modules: selectedModules,
        }
      );

      const siteCount =
        response.data?.siteModuleCount ??
        selectedModules.filter(
          (module) =>
            module.access_type === "SITE"
        ).length;

      const appCount =
        response.data?.appPageCount ??
        selectedModules.filter(
          (module) =>
            module.access_type === "APP"
        ).length;

      await Swal.fire({
        icon: "success",
        title: "Saved",
        html: `
          <div style="text-align:left">
            <div><strong>Website modules:</strong> ${siteCount}</div>
            <div><strong>App pages:</strong> ${appCount}</div>
          </div>
        `,
      });

      navigate("/superAdmin/admin_Menu");
    } catch (error) {
      console.error(
        "[ADMIN MODULE SAVE ERROR]",
        error
      );

      await Swal.fire(
        "Error",
        error?.response?.data?.error ||
          error?.message ||
          "Unable to save module access",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const renderModuleCard = (module) => {
    const isAppPage =
      module.accessType === "APP";

    return (
      <label
        key={`${module.id}-${module.link}`}
        className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-3 transition ${
          checked[module.link]
            ? isAppPage
              ? "border-violet-300 bg-violet-50"
              : "border-blue-300 bg-blue-50"
            : "border-slate-200 bg-slate-50 hover:bg-slate-100"
        }`}
      >
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={
            !!checked[module.link]
          }
          onChange={() =>
            toggleOne(module.link)
          }
          disabled={loading || saving}
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-slate-800">
              {module.label}
            </span>

            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                isAppPage
                  ? "bg-violet-100 text-violet-700"
                  : "bg-blue-100 text-blue-700"
              }`}
            >
              {module.accessType}
            </span>
          </div>

          <div className="mt-1 break-all text-xs text-slate-500">
            ID: {module.id} •{" "}
            {module.link}
          </div>

          {module.requiresSelfieVerification ? (
            <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-700">
              Selecting this page automatically enables{" "}
              <strong>Selfie Verification</strong>.
            </div>
          ) : null}
        </div>
      </label>
    );
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="flex flex-col md:h-screen md:flex-row md:overflow-hidden">
        <SuperAdminNav />

        <main className="w-full md:h-full md:w-3/4 md:overflow-auto">
          <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 text-white">
            <div className="px-4 py-5 sm:px-6 lg:px-8">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/30">
                    <Boxes className="h-5 w-5" />
                  </div>

                  <div>
                    <h1 className="text-2xl font-semibold leading-tight">
                      Assign Site and App Access
                    </h1>

                    <p className="text-sm text-white/80">
                      Manage website sidebar modules and
                      Sentinel mobile-app pages
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to="/superAdmin/admin_Menu"
                    className="inline-flex items-center gap-2 rounded-lg bg-white/15 px-3 py-1.5 text-white ring-1 ring-white/30 hover:bg-white/20"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </Link>

                  <div className="text-sm text-white/90">
                    <Time />
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/30">
                  <Building2 className="h-4 w-4" />

                  {tenant?.tenantCode ||
                    "Tenant"}{" "}
                  —{" "}
                  {tenant?.databaseName ||
                    "-"}
                </span>

                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/30">
                  <UserIcon className="h-4 w-4" />
                  {admin?.username ||
                    username}
                </span>

                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/30">
                  <BadgeCheck className="h-4 w-4" />
                  Selected: {selectedCount}
                </span>

                <span className="inline-flex items-center rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/30">
                  Site: {selectedSiteCount}
                </span>

                <span className="inline-flex items-center rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/30">
                  App: {selectedAppCount}
                </span>
              </div>
            </div>
          </div>

          <div className="px-4 py-6 sm:px-6 lg:px-8">
            <form
              onSubmit={onSave}
              className="rounded-2xl border border-slate-200 bg-white shadow-sm"
            >
              <div className="flex flex-col gap-3 rounded-t-2xl border-b border-slate-200 bg-gradient-to-r from-cyan-50 to-teal-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <Boxes className="h-4 w-4 text-cyan-600" />

                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">
                      Module and App Access
                    </h3>

                    <p className="text-xs text-slate-500">
                      SITE records are stored separately
                      from APP-page access
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={toggleAll}
                  className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white shadow hover:bg-black disabled:opacity-60"
                  disabled={loading || saving}
                >
                  {allChecked
                    ? "Unselect All"
                    : "Select All"}
                </button>
              </div>

              <div className="space-y-6 p-4">
                {loading ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">
                    Loading module access...
                  </div>
                ) : (
                  Object.entries(
                    groupedModules
                  ).map(
                    ([category, items]) => {
                      const categoryAllChecked =
                        items.every(
                          (module) =>
                            checked[
                              module.link
                            ]
                        );

                      const isAppCategory =
                        category ===
                        "Sentinel App Pages";

                      return (
                        <section
                          key={category}
                          className="rounded-xl border border-slate-200 bg-white"
                        >
                          <div
                            className={`flex flex-col gap-3 rounded-t-xl border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${
                              isAppCategory
                                ? "bg-violet-50"
                                : "bg-slate-50"
                            }`}
                          >
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="text-sm font-semibold text-slate-900">
                                  {category}
                                </h4>

                                <span
                                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                    isAppCategory
                                      ? "bg-violet-100 text-violet-700"
                                      : "bg-blue-100 text-blue-700"
                                  }`}
                                >
                                  {isAppCategory
                                    ? "APP ACCESS"
                                    : "SITE ACCESS"}
                                </span>
                              </div>

                              <p className="mt-1 text-xs text-slate-500">
                                {items.length}{" "}
                                {items.length ===
                                1
                                  ? "item"
                                  : "items"}
                              </p>
                            </div>

                            <button
                              type="button"
                              onClick={() =>
                                toggleCategory(
                                  items
                                )
                              }
                              disabled={
                                loading ||
                                saving
                              }
                              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-800 shadow-sm hover:bg-slate-100 disabled:opacity-60"
                            >
                              {categoryAllChecked
                                ? "Unselect Section"
                                : "Select Section"}
                            </button>
                          </div>

                          <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2 lg:grid-cols-3">
                            {items.map(
                              renderModuleCard
                            )}
                          </div>

                          {isAppCategory &&
                          (
                            checked.CampusCheck ||
                            checked.Attendance
                          ) ? (
                            <div className="mx-4 mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                              <strong>
                                Selfie Verification enabled:
                              </strong>{" "}
                              Campus Check and Attendance require selfie verification, so that app page has been selected automatically.
                            </div>
                          ) : null}
                        </section>
                      );
                    }
                  )
                )}
              </div>

              <div className="flex flex-col gap-3 rounded-b-2xl border-t border-slate-200 bg-white/60 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-slate-500">
                  {selectedSiteCount} website
                  module
                  {selectedSiteCount === 1
                    ? ""
                    : "s"}{" "}
                  and {selectedAppCount} app
                  page
                  {selectedAppCount === 1
                    ? ""
                    : "s"}{" "}
                  selected
                </div>

                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60"
                  disabled={saving || loading}
                >
                  <SaveIcon className="h-4 w-4" />

                  {saving
                    ? "Saving..."
                    : "Save Access"}
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}